import { sessionSnapshot, subscribeSession } from '@/api/session';
import type { Commands } from "@/api/commands";
import type { ProviderInfoWire, ProviderInfosResponse } from "@/api/types";

export type ProviderInfos = {
  account_providers: ProviderInfoWire[];
  market_providers: ProviderInfoWire[];
  engines: ProviderInfoWire[];
  strategies: ProviderInfoWire[];
  notification_channels: ProviderInfoWire[];
};

type RequestFn = Pick<Commands, "listProviderInfos">;

let cache: ProviderInfos | null = null;
let inflight: Promise<ProviderInfos> | null = null;
let generation = sessionSnapshot().generation;
subscribeSession(() => { const next = sessionSnapshot().generation; if (next !== generation) { generation = next; cache = null; inflight = null; } });

function normalize(raw: ProviderInfosResponse | null | undefined): ProviderInfos {
  return {
    account_providers: raw?.account_providers ?? [],
    market_providers: raw?.market_providers ?? [],
    engines: raw?.engines ?? [],
    strategies: raw?.strategies ?? [],
    notification_channels: raw?.notification_channels ?? [],
  };
}

/** Fetch once per session; shared across ExchangeMark instances. Retries after failure. */
export function ensureProviderInfos(request: RequestFn): Promise<ProviderInfos> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = (async () => {
      try {
        const current = generation;
        const raw = (await request.listProviderInfos()) as ProviderInfosResponse;
        if (current !== generation) throw new Error("Session changed");
        cache = normalize(raw);
        return cache;
      } catch (e) {
        inflight = null;
        throw e;
      }
    })();
  }
  return inflight;
}

/** Ids are unique across categories — search all lists. */
export function findProviderInfo(
  infos: ProviderInfos | null | undefined,
  provider: string | undefined
): ProviderInfoWire | null {
  if (!infos || !provider) return null;
  return (
    infos.account_providers.find((i) => i.id === provider) ??
    infos.market_providers.find((i) => i.id === provider) ??
    infos.engines.find((i) => i.id === provider) ??
    infos.strategies.find((i) => i.id === provider) ??
    infos.notification_channels.find((i) => i.id === provider) ??
    null
  );
}

/** Optional template whose static_options all match the given config. */
export function matchProviderTemplate(
  info: ProviderInfoWire | null | undefined,
  config?: Record<string, unknown> | null
) {
  if (!info?.templates?.length || !config) return null;
  for (const t of info.templates) {
    let match = true;
    for (const [k, v] of Object.entries(t.static_options ?? {})) {
      if (String(config[k] ?? "") !== v) {
        match = false;
        break;
      }
    }
    if (match) return t;
  }
  return null;
}
